import type { ReactNode } from "react";
import type { FieldValues, Path } from "react-hook-form";

import {
  Calendar,
  Checkbox,
  DateField,
  DatePicker,
  DateRangePicker,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  NumberField,
  RangeCalendar,
  Select,
  TextField,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

/*
 * Form fields for the app's react-hook-form + zod forms. Each one reads the form from `FormProvider`
 * (`useFormContext`), so a form keeps `useForm({ resolver: zodResolver(...) })` and the `mode` it has today.
 * Fields sit on a modal or sheet, so they use the `secondary` variant.
 */

export function TextInputField<T extends FieldValues>({
  name,
  label,
  placeholder,
  description,
  type,
  isDisabled,
  isRequired,
  isMono,
  dir,
}: {
  name: Path<T>;
  label: string;
  placeholder?: string;
  description?: ReactNode;
  type?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isMono?: boolean;
  dir?: "rtl" | "ltr";
}) {
  const { control } = useFormContext<T>();
  const { field, fieldState } = useController({
    control,
    name,
    disabled: isDisabled,
  });

  return (
    <TextField
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      name={field.name}
      type={type}
      value={(field.value as string | undefined) ?? ""}
      onBlur={field.onBlur}
      onChange={field.onChange}
    >
      <Label>{label}</Label>
      <Input
        ref={field.ref}
        className={isMono ? "font-mono" : undefined}
        dir={dir}
        lang={dir === "rtl" ? "ar" : undefined}
        placeholder={placeholder}
        variant="secondary"
      />
      {description ? <Description>{description}</Description> : null}
      <FieldError>{fieldState.error?.message}</FieldError>
    </TextField>
  );
}

/** A number input; an empty field holds `null`. */
export function NumberInputField<T extends FieldValues>({
  name,
  label,
  description,
  placeholder,
}: {
  name: Path<T>;
  label: string;
  description?: string;
  placeholder?: string;
}) {
  const { control } = useFormContext<T>();
  const { field, fieldState } = useController({ control, name });
  const value = field.value as number | null;

  return (
    <NumberField
      className="flex flex-col gap-1.5"
      isInvalid={fieldState.invalid}
      name={field.name}
      value={value ?? Number.NaN}
      variant="secondary"
      onBlur={field.onBlur}
      onChange={(next) => field.onChange(Number.isNaN(next) ? null : next)}
    >
      <Label>{label}</Label>
      <NumberField.Group>
        <NumberField.Input placeholder={placeholder} />
      </NumberField.Group>
      {description ? <Description>{description}</Description> : null}
      <FieldError>{fieldState.error?.message}</FieldError>
    </NumberField>
  );
}

export function CheckboxField<T extends FieldValues>({
  name,
  label,
  isDisabled,
}: {
  name: Path<T>;
  label: string;
  isDisabled?: boolean;
}) {
  const { watch, setValue } = useFormContext<T>();

  return (
    <Checkbox
      isDisabled={isDisabled}
      isSelected={Boolean(watch(name))}
      variant="secondary"
      onChange={(selected) =>
        setValue(name, selected as never, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    >
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {label}
      </Checkbox.Content>
    </Checkbox>
  );
}

export function SelectField<T extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  description,
  isDisabled,
}: {
  name: Path<T>;
  label: string;
  options: Array<{ id: string; label: string }>;
  placeholder?: string;
  description?: ReactNode;
  isDisabled?: boolean;
}) {
  const { watch, setValue, getFieldState, formState } = useFormContext<T>();
  const { error, invalid } = getFieldState(name, formState);

  return (
    <Select
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={invalid}
      placeholder={placeholder}
      selectedKey={(watch(name) as string | undefined) || null}
      variant="secondary"
      onSelectionChange={(key) =>
        setValue(name, String(key) as never, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      {description ? <Description>{description}</Description> : null}
      <FieldError>{error?.message}</FieldError>
      <Select.Popover>
        <ListBox>
          {options.map((option) => (
            <ListBox.Item
              key={option.id}
              id={option.id}
              textValue={option.label}
            >
              {option.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

/** A text field with plain `value`/`onChange`, for forms that keep their state in `useState`. */
export function PlainTextField({
  name,
  label,
  value,
  onChange,
  placeholder,
  description,
  type,
  isRequired,
  isMono,
  dir,
}: {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  description?: ReactNode;
  type?: string;
  isRequired?: boolean;
  isMono?: boolean;
  dir?: "rtl" | "ltr";
}) {
  return (
    <TextField
      className="flex flex-col gap-1.5"
      isRequired={isRequired}
      name={name}
      type={type}
      validationBehavior="native"
      value={value}
      onChange={onChange}
    >
      <Label>{label}</Label>
      <Input
        className={isMono ? "font-mono" : undefined}
        dir={dir}
        lang={dir === "rtl" ? "ar" : undefined}
        placeholder={placeholder}
        variant="secondary"
      />
      {description ? <Description>{description}</Description> : null}
      <FieldError />
    </TextField>
  );
}

const toCalendarDate = (day: string) => (day === "" ? null : parseDate(day));

/**
 * A date picker with plain `value`/`onChange`, for forms that keep their state in `useState`.
 * Days are `YYYY-MM-DD` strings, `""` when empty.
 */
export function PlainDateField({
  name,
  label,
  value,
  onChange,
  description,
  minValue,
}: {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: ReactNode;
  minValue?: string;
}) {
  const minDay = useMemo(
    () => (minValue ? parseDate(minValue) : undefined),
    [minValue],
  );

  return (
    <DatePicker
      className="flex flex-col gap-1.5"
      minValue={minDay}
      name={name}
      validationBehavior="aria"
      value={toCalendarDate(value)}
      onChange={(day) => onChange(day?.toString() ?? "")}
    >
      <Label>{label}</Label>
      <DateField.Group fullWidth variant="secondary">
        <DateField.Input>
          {(segment) => <DateField.Segment segment={segment} />}
        </DateField.Input>
        <DateField.Suffix>
          <DatePicker.Trigger>
            <DatePicker.TriggerIndicator />
          </DatePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      {description ? <Description>{description}</Description> : null}
      <FieldError />
      <DatePicker.Popover>
        <Calendar aria-label={label} minValue={minDay}>
          <Calendar.Header>
            <Calendar.YearPickerTrigger>
              <Calendar.YearPickerTriggerHeading />
              <Calendar.YearPickerTriggerIndicator />
            </Calendar.YearPickerTrigger>
            <Calendar.NavButton slot="previous" />
            <Calendar.NavButton slot="next" />
          </Calendar.Header>
          <Calendar.Grid>
            <Calendar.GridHeader>
              {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
            </Calendar.GridHeader>
            <Calendar.GridBody>
              {(date) => <Calendar.Cell date={date} />}
            </Calendar.GridBody>
          </Calendar.Grid>
          <Calendar.YearPickerGrid>
            <Calendar.YearPickerGridBody>
              {({ year }) => <Calendar.YearPickerCell year={year} />}
            </Calendar.YearPickerGridBody>
          </Calendar.YearPickerGrid>
        </Calendar>
      </DatePicker.Popover>
    </DatePicker>
  );
}

export type DayRange = { start: string; end: string };

/**
 * A date range picker with plain `value`/`onChange`, for forms that keep their state in `useState`.
 * Days are `YYYY-MM-DD` strings; the range is empty when either end is `""`.
 */
export function PlainDateRangeField({
  name,
  label,
  value,
  onChange,
  description,
}: {
  name: string;
  label: string;
  value: DayRange;
  onChange: (value: DayRange) => void;
  description?: ReactNode;
}) {
  return (
    <DateRangePicker
      className="flex flex-col gap-1.5"
      endName={`${name}End`}
      startName={`${name}Start`}
      validationBehavior="aria"
      value={
        value.start === "" || value.end === ""
          ? null
          : { start: parseDate(value.start), end: parseDate(value.end) }
      }
      onChange={(range) =>
        onChange({
          start: range?.start.toString() ?? "",
          end: range?.end.toString() ?? "",
        })
      }
    >
      <Label>{label}</Label>
      <DateField.Group fullWidth variant="secondary">
        <DateField.Input slot="start">
          {(segment) => <DateField.Segment segment={segment} />}
        </DateField.Input>
        <DateRangePicker.RangeSeparator />
        <DateField.Input slot="end">
          {(segment) => <DateField.Segment segment={segment} />}
        </DateField.Input>
        <DateField.Suffix>
          <DateRangePicker.Trigger>
            <DateRangePicker.TriggerIndicator />
          </DateRangePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      {description ? <Description>{description}</Description> : null}
      <FieldError />
      <DateRangePicker.Popover>
        <RangeCalendar aria-label={label}>
          <RangeCalendar.Header>
            <RangeCalendar.YearPickerTrigger>
              <RangeCalendar.YearPickerTriggerHeading />
              <RangeCalendar.YearPickerTriggerIndicator />
            </RangeCalendar.YearPickerTrigger>
            <RangeCalendar.NavButton slot="previous" />
            <RangeCalendar.NavButton slot="next" />
          </RangeCalendar.Header>
          <RangeCalendar.Grid>
            <RangeCalendar.GridHeader>
              {(day) => (
                <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>
              )}
            </RangeCalendar.GridHeader>
            <RangeCalendar.GridBody>
              {(date) => <RangeCalendar.Cell date={date} />}
            </RangeCalendar.GridBody>
          </RangeCalendar.Grid>
          <RangeCalendar.YearPickerGrid>
            <RangeCalendar.YearPickerGridBody>
              {({ year }) => <RangeCalendar.YearPickerCell year={year} />}
            </RangeCalendar.YearPickerGridBody>
          </RangeCalendar.YearPickerGrid>
        </RangeCalendar>
      </DateRangePicker.Popover>
    </DateRangePicker>
  );
}

/** A checkbox with plain `isSelected`/`onChange`, for forms that keep their state in `useState`. */
export function PlainCheckbox({
  label,
  isSelected,
  onChange,
}: {
  label: string;
  isSelected: boolean;
  onChange: (selected: boolean) => void;
}) {
  return (
    <Checkbox isSelected={isSelected} variant="secondary" onChange={onChange}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {label}
      </Checkbox.Content>
    </Checkbox>
  );
}
